/**
 * AffordabilityHero — Consolidated affordability hero for the Buyer Passport.
 *
 * Layout: ONE full-width horizontal row with three sections separated by thin
 * vertical dividers. Responsive: sections stack on narrow/phone screens.
 *
 *  Left (~25–28%):  ESTIMATED QUALIFICATION eyebrow → large $ figure →
 *                   "Sill estimate · ≈ $X/mo" subtitle
 *  Middle (~44%):   AFFORDABILITY RANGE eyebrow → target label aligned above
 *                   the orange marker → horizontal range track with green fill/knob →
 *                   "$X comfortable" … "$X stretch" labels
 *  Right (~28%):    VERIFICATION eyebrow → 4 nodes laid out horizontally
 *
 * Spec invariants (§2 / task 660–663):
 *   - Never renders $0 or a fabricated band.
 *   - When overlay is absent (pending / DNQ): estimate-only bar from buyingPowerMax.
 *   - When no data at all: estimate-pending presentation (no money figure).
 *   - Bank and Lender ladder rungs are always locked/coming-soon here.
 *
 * Data source: the affordability overlay outcome the dashboard profile read
 * assembles (bandLow/bandHigh/sillQualPrice from DashboardSnapshot, when
 * available) plus buyingPowerMax + targetPrice + monthlyPaymentMax from the
 * same snapshot.
 */

import type { DashboardSnapshot } from "../../../lib/dashboard";
import { deriveHighestVerificationTier } from "../../../lib/dashboard";
import { cn } from "../../../lib/utils";
import { TierLadder } from "./rows/tier-ladder";
import {
  deriveHeroGeometry,
  type HeroGeometry,
  type HeroOverlayInput,
} from "./affordability-hero-geometry";

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface AffordabilityHeroProps {
  snapshot: DashboardSnapshot;
  /** Called when the user clicks the Bank rung to verify. Optional. */
  onConnectBank?: () => void;
  /** Called when the user clicks the Doc rung to verify. Optional. */
  onUploadDocuments?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Format a dollar amount as a compact monthly string, e.g. "$3,180/mo". */
function fmtMonthly(dollars: number): string {
  return `$${Math.round(dollars).toLocaleString()}/mo`;
}

function targetMarkerAlignClass(targetPct: number): string {
  if (targetPct <= 10) {
    return "translate-x-0 items-start text-left";
  }

  if (targetPct >= 90) {
    return "-translate-x-full items-end text-right";
  }

  return "-translate-x-1/2 items-center text-center";
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AffordabilityHero renders the consolidated affordability section for the
 * Buyer Passport as a single horizontal 3-column row.
 */
export function AffordabilityHero({
  snapshot,
  onConnectBank,
  onUploadDocuments,
}: AffordabilityHeroProps) {
  const overlayInput: HeroOverlayInput = {
    bandLow: snapshot.affordabilityBandLow ?? null,
    bandHigh: snapshot.affordabilityBandHigh ?? null,
    sillQualPrice: snapshot.sillQualPrice ?? null,
    buyingPowerMax: snapshot.buyingPowerMax,
    targetPrice: snapshot.targetPrice,
  };

  const geometry = deriveHeroGeometry(overlayInput);
  const highestTier = deriveHighestVerificationTier(snapshot);

  // Derive "% fit" when we have both a qual price and a target
  const fitPct =
    geometry !== null &&
    geometry.kind === "overlay" &&
    snapshot.targetPrice > 0 &&
    geometry.targetPct !== null
      ? Math.round(
          Math.min(100, (snapshot.sillQualPrice! / snapshot.targetPrice) * 100),
        )
      : null;

  return (
    <section
      aria-labelledby="affordability-hero-headline"
      className="rounded-xl bg-popover"
      data-slot="affordability-hero"
      data-testid="affordability-hero"
    >
      {/* ── Three-column row: responsive stack on mobile ─────────────────── */}
      <div className="flex flex-col sm:flex-row sm:divide-x sm:divide-border/60">
        {/* ── LEFT: Estimated Qualification ─────────────────────────────── */}
        <div className="flex min-w-0 flex-col justify-center gap-2 p-4 sm:w-[27%] sm:shrink-0 sm:p-5">
          <HeroQualFigure
            geometry={geometry}
            monthlyPaymentMax={snapshot.monthlyPaymentMax}
          />
        </div>

        {/* ── MIDDLE: Affordability Range ───────────────────────────────── */}
        <div className="min-w-0 flex-1 border-t border-border/60 p-4 sm:border-t-0 sm:p-5">
          <AffordabilityRangeSection geometry={geometry} fitPct={fitPct} />
        </div>

        {/* ── RIGHT: Verification ───────────────────────────────────────── */}
        <div className="flex min-w-0 flex-col justify-center gap-3 border-t border-border/60 p-4 sm:w-[27%] sm:shrink-0 sm:border-t-0 sm:p-5">
          <p className="text-[0.625rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground">
            Verification
          </p>
          <TierLadder
            current={highestTier}
            // Bank is locked at the page-level hero for a reason that is
            // neither absence nor rollout: it needs per-row action, so it is
            // simply not climbable from THIS surface. The reason map lets the
            // hero say that in its own words rather than borrowing a
            // capability sentence that would be wrong here. (Lender is locked
            // for every row and TierLadder hardcodes it.)
            unavailable={{
              "bank-verified":
                "Connect a bank from the input you want to verify.",
            }}
            onClimb={(tier) => {
              if (tier === "document-backed") {
                onUploadDocuments?.();
              } else if (tier === "bank-verified") {
                onConnectBank?.();
              }
            }}
          />
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Left-column: qualification figure + "Sill estimate · ≈ $X/mo" subtitle.
 */
function HeroQualFigure({
  geometry,
  monthlyPaymentMax,
}: {
  geometry: HeroGeometry | null;
  monthlyPaymentMax: number;
}) {
  const monthlySubtitle =
    monthlyPaymentMax > 0
      ? `Sill estimate · ≈ ${fmtMonthly(monthlyPaymentMax)}`
      : "Sill estimate";

  if (geometry === null) {
    return (
      <div>
        <p
          className="text-[0.625rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground"
          id="affordability-hero-headline"
        >
          Estimated Qualification
        </p>
        <p className="mt-3 text-2xl font-semibold leading-tight text-muted-foreground">
          Pending
        </p>
        <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
          Add income, balances, and debts to see your range.
        </p>
      </div>
    );
  }

  if (geometry.kind === "overlay") {
    return (
      <div>
        <p
          className="text-[0.625rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground"
          id="affordability-hero-headline"
        >
          Estimated Qualification
        </p>
        <h2
          className="display-figure mt-3 break-words text-[2.5rem] leading-[0.95] text-foreground sm:text-[3rem]"
          data-testid="hero-qual-price"
        >
          {geometry.qualPriceDisplay}
        </h2>
        <p className="mt-2 text-xs leading-5 text-muted-foreground">
          {monthlySubtitle}
        </p>
      </div>
    );
  }

  // estimate path
  return (
    <div>
      <p
        className="text-[0.625rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground"
        id="affordability-hero-headline"
      >
        Estimated Qualification
      </p>
      <h2
        className="display-figure mt-3 break-words text-[2.5rem] leading-[0.95] text-foreground sm:text-[3rem]"
        data-testid="hero-estimate"
      >
        {geometry.estimateDisplay}
      </h2>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">
        {monthlySubtitle}
      </p>
    </div>
  );
}

/**
 * Middle column: AFFORDABILITY RANGE eyebrow + fit summary, then the range bar
 * with the target label anchored to its marker and comfortable/stretch labels below.
 */
function AffordabilityRangeSection({
  geometry,
  fitPct,
}: {
  geometry: HeroGeometry | null;
  fitPct: number | null;
}) {
  return (
    <div className="space-y-3">
      {/* Eyebrow row */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-[0.625rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground">
          Affordability Range
        </p>
        {fitPct !== null ? (
          <p
            className="text-[0.625rem] font-semibold leading-none text-primary tabular-nums"
            data-testid="hero-fit-label"
          >
            {fitPct}% fit
          </p>
        ) : null}
      </div>

      {/* Range bar */}
      {geometry !== null ? (
        <HeroRangeBar geometry={geometry} />
      ) : (
        <EstimatePendingBar />
      )}
    </div>
  );
}

/**
 * HeroRangeBar renders the actual affordability bar for overlay or
 * estimate-only paths, with darker green fill and x-axis labels.
 */
function HeroRangeBar({ geometry }: { geometry: HeroGeometry }) {
  if (geometry.kind === "overlay") {
    return (
      <div data-testid="hero-range-bar-wrapper">
        <div className="relative pt-6" data-testid="hero-marker-stack">
          {geometry.targetPct !== null && geometry.targetDisplay ? (
            <div
              className={cn(
                "absolute top-0 z-10 flex min-w-max flex-col gap-1 text-[0.625rem] font-semibold leading-none text-muted-foreground tabular-nums",
                targetMarkerAlignClass(geometry.targetPct),
              )}
              data-testid="hero-target-marker-label"
              style={{ left: `${geometry.targetPct}%` }}
            >
              <span>Target {geometry.targetDisplay}</span>
            </div>
          ) : null}

          <div
            aria-label="Affordability range bar"
            className="relative h-3 overflow-visible rounded-full bg-primary/15"
            data-testid="hero-range-bar"
            role="img"
          >
            {/* Comfortable→stretch band span — darker green fill */}
            {geometry.bandWidthPct > 0 ? (
              <div
                className="absolute top-0 h-full rounded-full bg-primary"
                data-testid="hero-band-span"
                style={{
                  left: `${geometry.bandLeftPct}%`,
                  width: `${geometry.bandWidthPct}%`,
                }}
              />
            ) : null}

            {/* Qual-price tick — knob at the qualification point */}
            <div
              aria-label={`Qualification price: ${geometry.qualPriceDisplay}`}
              className="absolute top-1/2 h-5 w-1.5 -translate-y-1/2 -translate-x-1/2 rounded-full bg-primary shadow-sm"
              data-testid="hero-qual-price-tick"
              style={{ left: `${geometry.qualPricePct}%` }}
            />

            {/* Target marker */}
            {geometry.targetPct !== null ? (
              <div
                aria-label={`Target: ${geometry.targetDisplay}`}
                className="absolute top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-warning"
                data-testid="hero-target-marker"
                style={{ left: `${geometry.targetPct}%` }}
              />
            ) : null}
          </div>
        </div>

        {/* X-axis labels: band low left ("comfortable"), band high right ("stretch") */}
        <div
          className="relative mt-1.5 flex justify-between"
          aria-hidden="true"
        >
          {geometry.bandWidthPct > 0 ? (
            <>
              <span
                className="text-[10px] text-muted-foreground"
                data-testid="hero-bar-label-low"
              >
                {geometry.bandDisplay.split("–")[0]} comfortable
              </span>
              <span
                className="text-[10px] text-muted-foreground"
                data-testid="hero-bar-label-high"
              >
                {geometry.bandDisplay.split("–")[1] ?? ""} stretch
              </span>
            </>
          ) : (
            <span
              className="absolute -translate-x-1/2 text-[10px] text-muted-foreground"
              style={{ left: `${geometry.qualPricePct}%` }}
            >
              {geometry.qualPriceDisplay}
            </span>
          )}
        </div>
      </div>
    );
  }

  // estimate path — single fill bar with darker green
  return (
    <div data-testid="hero-range-bar-wrapper">
      <div className="relative pt-6" data-testid="hero-marker-stack">
        {geometry.targetPct !== null && geometry.targetDisplay ? (
          <div
            className={cn(
              "absolute top-0 z-10 flex min-w-max flex-col gap-1 text-[0.625rem] font-semibold leading-none text-muted-foreground tabular-nums",
              targetMarkerAlignClass(geometry.targetPct),
            )}
            data-testid="hero-target-marker-label"
            style={{ left: `${geometry.targetPct}%` }}
          >
            <span>Target {geometry.targetDisplay}</span>
          </div>
        ) : null}

        <div
          aria-label="Affordability estimate bar"
          className="relative h-3 overflow-visible rounded-full bg-primary/15"
          data-testid="hero-range-bar"
          role="img"
        >
          <div
            className="absolute top-0 h-full rounded-full bg-primary"
            data-testid="hero-estimate-fill"
            style={{ width: `${geometry.estimatePct}%` }}
          />

          {geometry.targetPct !== null ? (
            <div
              aria-label={`Target: ${geometry.targetDisplay}`}
              className="absolute top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-warning"
              data-testid="hero-target-marker"
              style={{ left: `${geometry.targetPct}%` }}
            />
          ) : null}
        </div>
      </div>

      {/* X-axis labels for estimate path */}
      <div className="relative mt-1.5 h-4" aria-hidden="true">
        <span
          className="absolute -translate-x-1/2 text-[10px] text-muted-foreground"
          data-testid="hero-bar-label-estimate"
          style={{ left: `${geometry.estimatePct}%` }}
        >
          {geometry.estimateDisplay}
        </span>
      </div>
    </div>
  );
}

/** Renders when geometry is null — no data, no money figure. */
function EstimatePendingBar() {
  return (
    <div
      className="relative h-3 rounded-full bg-primary/10"
      data-testid="hero-pending-bar"
      aria-label="Affordability pending"
    />
  );
}
